import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, SectionTitle, Overline, EmptyState } from "@/components/portal/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Copy, Trash2, Check, Mail } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("athlete");
  const [athleteId, setAthleteId] = useState("");
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [u, i, a] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/invitations"),
        api.get("/athletes"),
      ]);
      setUsers(u.data); setInvites(i.data); setAthletes(a.data);
    } catch {
      /* non-admin (e.g. reviewer) — no access to team management */
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const body = { email: email.trim(), role };
      if (role === "athlete") body.athlete_id = athleteId;
      const { data } = await api.post("/admin/invitations", body);
      setCreated(data);
      setEmail(""); setAthleteId("");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Invite link copied");
  };

  const revoke = async (id) => {
    await api.delete(`/admin/invitations/${id}`);
    toast.success("Invitation revoked");
    load();
  };

  const removeUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const athleteName = (id) => athletes.find((a) => a.id === id)?.name || id;

  return (
    <div>
      <SectionTitle
        sub="Invite admins and athletes. Links are shown once — share them through a trusted channel."
        right={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); setCreated(null); setError(""); }}>
            <DialogTrigger asChild>
              <Button className="bg-white text-black hover:bg-white/90 font-semibold gap-2" data-testid="invite-open-button">
                <UserPlus className="h-4 w-4" /> Invite
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle className="font-display text-2xl">Invite to the portal</DialogTitle></DialogHeader>
              {created ? (
                <div className="space-y-3" data-testid="invite-created">
                  <p className="text-sm text-muted-foreground">Invitation created for <span className="text-white">{created.email}</span>. Copy this link and send it privately — it won't be shown again.</p>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={created.accept_url} className="bg-secondary border-border text-xs" data-testid="invite-link-value" />
                    <Button size="icon" variant="outline" className="border-border shrink-0" onClick={() => copyLink(created.accept_url)} data-testid="invite-copy-button">
                      {copied ? <Check className="h-4 w-4 text-[#E1FF00]" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button variant="outline" className="border-border w-full" onClick={() => setCreated(null)}>Invite another</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-secondary border-border" data-testid="invite-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger className="bg-secondary border-border" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="athlete">Athlete (own profile only)</SelectItem>
                        <SelectItem value="admin">Admin (full back office)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {role === "athlete" && (
                    <div className="space-y-2">
                      <Label>Link to athlete</Label>
                      <Select value={athleteId} onValueChange={setAthleteId}>
                        <SelectTrigger className="bg-secondary border-border" data-testid="invite-athlete-select"><SelectValue placeholder="Select athlete" /></SelectTrigger>
                        <SelectContent>
                          {athletes.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.school}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {error && <div className="text-sm text-[#FF6666]">{error}</div>}
                  <DialogFooter>
                    <Button type="submit" className="bg-white text-black hover:bg-white/90 font-semibold" data-testid="invite-submit-button">Create invite link</Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        }
      >
        Team & Access
      </SectionTitle>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-6">
          <Overline className="mb-4">Members ({users.length})</Overline>
          <div className="space-y-2" data-testid="users-list">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between rounded-md border border-border px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">{u.name || u.email}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email} · {u.role}{u.athlete_id ? ` · ${athleteName(u.athlete_id)}` : ""}</div>
                </div>
                <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-[#FF3333]" onClick={() => removeUser(u.id)} data-testid={`delete-user-${u.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </Panel>

        <Panel className="p-6">
          <Overline className="mb-4">Pending invitations ({invites.length})</Overline>
          {invites.length === 0 ? (
            <EmptyState icon={Mail} title="No pending invitations" hint="Invite an admin or athlete to get started." />
          ) : (
            <div className="space-y-2" data-testid="invites-list">
              {invites.map((i) => (
                <div key={i.id} className="flex items-center justify-between rounded-md border border-border px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white truncate">{i.email}</div>
                    <div className="text-xs text-muted-foreground truncate">{i.role}{i.athlete_id ? ` · ${athleteName(i.athlete_id)}` : ""}</div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-[#FF3333]" onClick={() => revoke(i.id)} data-testid={`revoke-invite-${i.id}`}>
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
